import React, { useEffect, useState } from 'react';
import { Card, Button, Table, Badge, Spinner, Modal, Form, InputGroup, FormControl } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt, faMoneyBillWave, faEye, faPercentage } from '@fortawesome/free-solid-svg-icons';
import { getSecretaryInfo, getSecretaryRefunds, createSecretaryRefund } from '../../services/Secretary_service';
import RibModal from '../client/avoir/RibModal';
import '../../css/SecretaryDashboard.css';

const SecretaryDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [secretary, setSecretary] = useState<any>(null);
    const [entreprise, setEntreprise] = useState<any>(null);
    const [refunds, setRefunds] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showRibModal, setShowRibModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedRefund, setSelectedRefund] = useState<any>(null);
    const [amount, setAmount] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const secretaryId = localStorage.getItem('secretaryId');
    const entrepriseName = localStorage.getItem('entrepriseName');

    useEffect(() => {
        if (!secretaryId) {
            navigate('/secretary-login');
            return;
        }
        fetchData();
    }, [secretaryId, navigate]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const secretaryData = await getSecretaryInfo(Number(secretaryId));
            setSecretary(secretaryData);

            // Récupérer les demandes de retrait
            const refundsResponse = await getSecretaryRefunds(Number(secretaryId));
            if (refundsResponse.status === 200) {
                setRefunds(refundsResponse.data);
            }
        } catch (error) {
            console.error("Erreur chargement données:", error);
            toast.error("Erreur lors du chargement des données");
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        toast.success("Déconnexion réussie");
        navigate('/secretary-login');
    };

    const handleRequestRefund = () => {
        if (!secretary || secretary.solde < 50) {
            toast.error("Solde insuffisant pour un retrait (minimum 50€)");
            return;
        }
        setShowRibModal(true);
    };

    const handleRibConfirm = async (rib: string, bic?: string) => {
        setSubmitting(true);
        setShowRibModal(false);

        try {
            const numericAmount = parseFloat(amount);
            
            if (numericAmount > secretary.solde) {
                toast.error("Montant supérieur au solde disponible");
                setSubmitting(false);
                return;
            }

            if (numericAmount < 50) {
                toast.error("Montant minimum : 50€");
                setSubmitting(false);
                return;
            }

            const response = await createSecretaryRefund({
                rib,
                bic: bic || undefined,
                amount: numericAmount,
            });

            if (response.status === 201) {
                toast.success("Demande de retrait soumise avec succès");
                setAmount('');
                fetchData();
            }
        } catch (error: any) {
            const message = error.response?.data?.message || "Erreur lors de la soumission";
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    const showRefundDetails = (refund: any) => {
        setSelectedRefund(refund);
        setShowDetailsModal(true);
    };

    const getStatusBadge = (status: string) => {
        const mapping: Record<string, { className: string; text: string }> = {
            pending: { className: 'badgePending', text: 'En attente' },
            accepted: { className: 'badge-primary-light', text: 'Acceptée' },
            rejected: { className: 'badge-accent', text: 'Rejetée' },
        };
        const cfg = mapping[status] || { className: 'badge-accent', text: status };
        return <Badge className={cfg.className}>{cfg.text}</Badge>;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('fr-FR');
    };

    useEffect(() => {
    console.log('🔑 Token dans localStorage:', localStorage.getItem('token'));
    console.log('📧 Secretary ID:', localStorage.getItem('secretaryId'));
}, []);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    return (
        <>
            <ToastContainer />
            <div className="secretary-dashboard container-fluid p-4">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2>Tableau de bord Assistant</h2>
                        <p className="text-muted">
                            {secretary?.prenom} {secretary?.nom} - {entrepriseName}
                        </p>
                    </div>
                    <Button variant="outline-danger" onClick={handleLogout}>
                        <FontAwesomeIcon icon={faSignOutAlt} /> Déconnexion
                    </Button>
                </div>

                {/* Cartes d'informations */}
                <div className="row mb-4">
                    <div className="col-md-4">
                        <Card className="balance-card">
                            <Card.Body>
                                <h6 className="card-title">Solde disponible</h6>
                                <h3 className="text-primary">{secretary?.solde?.toFixed(2) || '0.00'} €</h3>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-md-4">
                        <Card className="balance-card">
                            <Card.Body>
                                <h6 className="card-title">Commission</h6>
                                <h3 className="text-success">
                                    <FontAwesomeIcon icon={faPercentage} /> {secretary?.sharedPercentageReferral?.toFixed(2) || '0'}%
                                </h3>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-md-4">
                        <Card className="balance-card">
                            <Card.Body>
                                <h6 className="card-title">Retraits en attente</h6>
                                <h3 className="text-warning">
                                    {refunds.filter(r => r.status === 'pending').length}
                                </h3>
                            </Card.Body>
                        </Card>
                    </div>
                </div>

                {/* Section demande de retrait */}
                <Card className="mb-4">
                    <Card.Body>
                        <h5>Demander un retrait</h5>
                        <Form.Group className="mb-3">
                            <Form.Label>Montant à retirer (minimum 50€)</Form.Label>
                            <InputGroup>
                                <FormControl
                                    type="number"
                                    placeholder="Ex: 150.50"
                                    value={amount}
                                    onChange={(e) => {
                                        setAmount(e.target.value);
                                        setErrorMessage('');
                                    }}
                                    min="50"
                                    step="0.01"
                                />
                                <InputGroup.Text>€</InputGroup.Text>
                            </InputGroup>
                            {errorMessage && <Form.Text className="text-danger">{errorMessage}</Form.Text>}
                        </Form.Group>
                        <Button 
                            variant="primary" 
                            onClick={handleRequestRefund}
                            disabled={!amount || parseFloat(amount) < 50 || submitting}
                        >
                            <FontAwesomeIcon icon={faMoneyBillWave} /> Demander un retrait
                        </Button>
                    </Card.Body>
                </Card>

                {/* Historique des retraits */}
                <Card>
                    <Card.Body>
                        <h5>Historique des retraits</h5>
                        <Table striped bordered hover responsive>
                            <thead>
                                <tr>
                                    <th>N° Demande</th>
                                    <th>Date</th>
                                    <th>Montant</th>
                                    <th>Statut</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {refunds.length > 0 ? (
                                    refunds.map((refund) => (
                                        <tr key={refund.id}>
                                            <td>
                                                <code className="badge badge-primary-light">
                                                    {refund.request_number || 'N/A'}
                                                </code>
                                            </td>
                                            <td>{formatDate(refund.createdAt)}</td>
                                            <td className="fw-bold">{Number(refund.amount).toFixed(2)} €</td>
                                            <td>{getStatusBadge(refund.status)}</td>
                                            <td>
                                                <Button 
                                                    size="sm" 
                                                    onClick={() => showRefundDetails(refund)}
                                                >
                                                    <FontAwesomeIcon icon={faEye} /> Détails
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={5} className="text-center">
                                            Aucune demande de retrait pour le moment
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            </div>

            {/* Modal RIB */}
            <RibModal
                visible={showRibModal}
                onCancel={() => setShowRibModal(false)}
                onConfirm={handleRibConfirm}
                loading={submitting}
                requireBic={true}
            />

            {/* Modal détails */}
            <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Détails de la demande</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedRefund && (
                        <div>
                            <div className="alert alert-info mb-3">
                                <strong>Numéro de demande:</strong>
                                <h5 className="mb-0 mt-1">
                                    <code style={{ fontSize: '1.2rem', color: '#2A919D' }}>
                                        {selectedRefund.request_number || 'N/A'}
                                    </code>
                                </h5>
                            </div>
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <strong>Date:</strong> {formatDate(selectedRefund.createdAt)}
                                </div>
                                <div className="col-md-6">
                                    <strong>Montant:</strong> {Number(selectedRefund.amount).toFixed(2)} €
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-md-12">
                                    <strong>Statut:</strong> {getStatusBadge(selectedRefund.status)}
                                </div>
                            </div>
                            {selectedRefund.rejection_reason && (
                                <div className="alert alert-danger">
                                    <strong>Motif du refus:</strong>
                                    <p className="mb-0 mt-2">{selectedRefund.rejection_reason}</p>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

export default SecretaryDashboard;